// preflightCheck.js — Kolt Printing colour-space gatekeeper.
//
// Runs entirely client-side BEFORE the upload network request is fired.
// Goal: block unsupported formats and WARN (never block) on obviously RGB
// rasters so the customer is not surprised on press. PDFs are analysed
// server-side.

export const CMYK_NOTICE =
  "Colour notice: this file is in RGB. We'll convert it to CMYK for press, so bright RGB tones " +
  "(neon greens, electric blues, pure reds) may print slightly duller. For exact colour, export as CMYK — " +
  "otherwise your proof will show the converted result.";

const RGB_EXTENSIONS_ALWAYS = new Set(["png", "gif", "webp", "bmp", "svg"]);
const ALLOWED_EXTENSIONS = new Set([
  "pdf", "tif", "tiff", "ai", "eps", "psd", "png", "jpg", "jpeg",
]);

export function extOf(filename) {
  return (filename || "").toLowerCase().split(".").pop();
}

// Inspect the start of a JPEG file looking for either:
//   (a) APP14 Adobe marker with transform byte === 2 (CMYK), or
//   (b) SOF0/SOF2 marker reporting 4 components (also CMYK).
// Either signal is sufficient.
async function jpegIsCmyk(file) {
  const buf = new Uint8Array(await file.slice(0, 64 * 1024).arrayBuffer());
  if (buf.length < 4 || buf[0] !== 0xff || buf[1] !== 0xd8) return false;
  let i = 2;
  while (i < buf.length - 4) {
    if (buf[i] !== 0xff) {
      i += 1;
      continue;
    }
    // Skip 0xFF padding bytes
    while (i < buf.length && buf[i] === 0xff) i += 1;
    const marker = buf[i];
    i += 1;
    if (marker === 0xd8 || marker === 0xd9) continue; // SOI/EOI: no payload
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue; // standalone
    if (i + 2 > buf.length) break;
    const segLen = (buf[i] << 8) | buf[i + 1];
    if (segLen < 2 || i + segLen > buf.length) break;

    // APP14 = 0xEE
    if (marker === 0xee && segLen >= 14) {
      const sig =
        buf[i + 2] === 0x41 &&
        buf[i + 3] === 0x64 &&
        buf[i + 4] === 0x6f &&
        buf[i + 5] === 0x62 &&
        buf[i + 6] === 0x65; // "Adobe"
      if (sig) {
        const transform = buf[i + 11];
        if (transform === 2) return true; // CMYK/YCCK
      }
    }
    // Start of Frame markers (SOF0..SOF15 except SOF4 DHT etc)
    const isSof =
      (marker >= 0xc0 && marker <= 0xc3) ||
      (marker >= 0xc5 && marker <= 0xc7) ||
      (marker >= 0xc9 && marker <= 0xcb) ||
      (marker >= 0xcd && marker <= 0xcf);
    if (isSof && segLen >= 6) {
      // [len(2) precision(1) height(2) width(2) numComponents(1) ...]
      const numComponents = buf[i + 7];
      if (numComponents === 4) return true;
      if (numComponents === 3 || numComponents === 1) return false;
    }
    i += segLen;
  }
  return false;
}

/**
 * Run the pre-flight check.
 *
 * Returns one of:
 *   { ok: true,  needsConversion: boolean, kind: "pdf"|"raster"|"vector", warning?: string }
 *   { ok: false, reason: string }   (unsupported format only)
 */
export async function preflightCmyk(file) {
  const ext = extOf(file.name);

  if (!ALLOWED_EXTENSIONS.has(ext)) {
    return {
      ok: false,
      reason: `Unsupported file format ".${ext}". Allowed: PDF, TIFF, AI, EPS, PSD, JPG.`,
    };
  }

  // Formats that are RGB by definition — upload proceeds, customer is warned.
  if (RGB_EXTENSIONS_ALWAYS.has(ext)) {
    return { ok: true, needsConversion: true, kind: "raster", warning: CMYK_NOTICE };
  }

  // JPEG: usually RGB but can be CMYK.
  if (ext === "jpg" || ext === "jpeg") {
    const cmyk = await jpegIsCmyk(file);
    return { ok: true, needsConversion: true, kind: "raster", warning: cmyk ? null : CMYK_NOTICE };
  }

  // PDF: colour analysis happens server-side (per-image colourspace + fill ops);
  // a byte-scan for /DeviceRGB flagged genuine CMYK exports, so no client verdict.
  if (ext === "pdf") {
    return { ok: true, needsConversion: false, kind: "pdf" };
  }

  // TIFF / AI / EPS / PSD — trust the customer at this layer.
  // The server-side normaliser will still run.
  if (ext === "tif" || ext === "tiff") {
    return { ok: true, needsConversion: true, kind: "raster" };
  }
  if (ext === "ai" || ext === "eps" || ext === "psd") {
    return { ok: true, needsConversion: true, kind: "vector" };
  }

  return { ok: true, needsConversion: false, kind: "pdf" };
}
